/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-underscore-dangle */
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import sizes from 'constants/sizes';
import { cleanValue } from 'constants/formats';

import { useTranslate } from 'hooks/useTranslate';
import { getDecimalScale } from 'utils/filters';
import { isMobileScreen } from 'utils/getScreenSize';

import { setModal } from 'modules/_shared/redux/modalActions';
import BeneficiarySell from 'modules/beneficiaries/modals/BeneficiarySell';
import getSimulatorValues from 'modules/beneficiaries/components/BeneficiarySocietyValueSimulator/utils/getSimulatorValues';
import { BeneficiarySellModalProvider } from 'modules/beneficiaries/modals/BeneficiarySell/context/BeneficiarySellModalContext';
import { useBeneficiaryHasTenderOffers } from 'modules/beneficiaries/pages/BeneficiaryDetails/hooks/useBeneficiaryHasTenderOffers';
import { getTenderOffers } from 'modules/transactions/redux/tenderOfferActions';
import BeneficiaryTenderOfferSelectorModal from 'modules/beneficiaries/modals/BeneficiaryTenderOfferSelectorModal';
import { BeneficiaryTenderOfferSelectorModalProvider } from 'modules/beneficiaries/modals/BeneficiaryTenderOfferSelectorModal/context/BeneficiaryTenderOfferSelectorModalContext';

import { State } from 'redux/initialState';
import { Beneficiary, Society, SocietyValue } from 'types';

import { useBeneficiaryIncentivesMarket } from './useBeneficiaryIncentivesMarket';
import {
  BeneficiaryIncentivePoint,
  BeneficiarySimulatorPoint,
  BeneficiarySocietyProps,
  BeneficiarySocietySummary,
  BeneficiaryVestingPoint,
  BeneficiaryWithPlans,
} from '../types/BeneficiarySocietyTypes';
import {
  getIncentivesData,
  getSummaryData,
  getVestingData,
} from '../utils/beneficiarySocietyCalculations';
import { chartOptions, convertData } from '../utils/beneficiarySocietyChart';
import {
  getIncentiveChart,
  hasPendingInvitations,
  hasPlanWithInvitation,
} from '../utils/beneficiarySocietyInvitations';

export const useBeneficiarySociety = ({
  beneficiary,
  society,
  beneficiaryAllPlans,
  setPage,
  simulatorValue,
  setSimulatorValue,
}: BeneficiarySocietyProps) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const { societyValue } = beneficiary;
  const configuration = useSelector(
    (state: State) => state.society?.actualSociety?.configuration
  );
  const tenderOffers = useSelector((state: State) => state.tenderOffers);

  const [decimalScale] = useState(
    societyValue ? getDecimalScale(societyValue.value) : 2
  );
  const [FDpercent, setFDpercent] = useState(0);

  const [hasIncentiveChart, setHasIncentiveChart] = useState(false);
  const [incentivesValues, setIncentivesValues] = useState<
    BeneficiaryIncentivePoint[]
  >([]);
  const [simulatorSocietyValue, setSimulatorSocietyValue] = useState<
    BeneficiarySimulatorPoint | undefined
  >();
  const [vestingValues, setVestingValues] = useState<BeneficiaryVestingPoint[]>(
    []
  );
  const [valuation, setValuation] = useState(0);
  const [summary, setSummary] = useState<BeneficiarySocietySummary>({
    units: 0,
    vestedUnits: 0,
    FDpercent: 0,
    exercisePrice: 0,
  });

  const [isAcceptButtonVisible, setIsAcceptButtonVisible] = useState(false);
  const [isMobile] = useState(() => isMobileScreen());

  const { data: tenderOffersData } = useBeneficiaryHasTenderOffers({
    beneficiaryId: beneficiary?._id || '',
    tenderOffers,
  });

  const { hasIncentivesMarketEnabled, handleOpenIncentivesMarket } =
    useBeneficiaryIncentivesMarket({ beneficiary, society });

  const handleSeeOffers = () => {
    dispatch(
      setModal(
        <BeneficiaryTenderOfferSelectorModalProvider
          beneficiaryId={beneficiary?._id || ''}
        >
          <BeneficiaryTenderOfferSelectorModal />
        </BeneficiaryTenderOfferSelectorModalProvider>
      )
    );
  };

  const handleChangeSlider = (value: number[]) => {
    const { FDpercent: currentFD, exercisePrice } = getSimulatorValues({
      society: society as Society,
      societyValue: societyValue as SocietyValue,
      beneficiaryAllPlans,
    });

    const newValue = cleanValue(value[0]);
    const valuationValue = (currentFD * newValue) / 100;
    const higherValue = Math.max(valuationValue, exercisePrice);

    setSimulatorValue(newValue);
    setValuation(higherValue);
  };

  const handleOpenSellView = () => {
    const SellModalProvider = BeneficiarySellModalProvider as FC<{
      children: React.ReactNode;
      beneficiary: Beneficiary;
      beneficiaryAllPlans: BeneficiaryWithPlans[];
      size?: typeof sizes.XL;
    }>;

    dispatch(
      setModal(
        <SellModalProvider
          beneficiary={beneficiary as Beneficiary}
          beneficiaryAllPlans={beneficiaryAllPlans}
          size={sizes.XL}
        >
          <BeneficiarySell />
        </SellModalProvider>
      )
    );
  };

  const chartDataInput = {
    incentivesValues,
    simulatorSocietyValue,
    vestingValues,
  };

  useEffect(() => {
    if (configuration) {
      const incentiveChartEnabled = getIncentiveChart(configuration);
      setHasIncentiveChart(incentiveChartEnabled);
    }
  }, [configuration]);

  useEffect(() => {
    if (beneficiaryAllPlans?.length) {
      const summaryData = getSummaryData(beneficiaryAllPlans, society);
      setFDpercent(summaryData.FDpercent);
      setSummary({ ...summaryData });

      const vestingData = getVestingData(
        beneficiaryAllPlans,
        summaryData,
        society,
        FDpercent
      );
      setVestingValues(vestingData);
    }
  }, [beneficiaryAllPlans]);

  useEffect(() => {
    if (vestingValues?.length > 0) {
      const incentivesData = getIncentivesData(
        beneficiaryAllPlans,
        society,
        vestingValues,
        FDpercent,
        summary
      );
      setIncentivesValues(incentivesData);
    }
  }, [vestingValues, summary]);

  useEffect(() => {
    if (
      simulatorValue &&
      simulatorValue > (societyValue?.value || 0) &&
      incentivesValues?.length
    ) {
      const lastDate = incentivesValues.reduce((acc, curr) => {
        if (new Date(curr.date) > new Date(acc)) return curr.date;
        return acc;
      }, incentivesValues[0].date);

      const simValueDate = new Date(lastDate);
      simValueDate.setMonth(simValueDate.getMonth() + 1);
      const incentive = +((+summary?.FDpercent * simulatorValue) / 100).toFixed(
        2
      );
      const newSocietyValue: BeneficiarySimulatorPoint = {
        date: simValueDate,
        value: simulatorValue,
        incentive,
      };
      setSimulatorSocietyValue(newSocietyValue);
    } else setSimulatorSocietyValue(undefined);
  }, [simulatorValue]);

  useEffect(() => {
    const hasPlansPending = !!(
      summary?.invitations?.length &&
      hasPlanWithInvitation(summary.invitations) &&
      hasPendingInvitations(summary.invitations)
    );

    const shouldShowAcceptButton = hasPlansPending;

    setIsAcceptButtonVisible(shouldShowAcceptButton);
  }, [summary]);

  useEffect(() => {
    if (society && beneficiaryAllPlans.length) {
      const { incentiveValue } = getSimulatorValues({
        society: society as Society,
        societyValue: societyValue as SocietyValue,
        beneficiaryAllPlans,
      });
      setValuation(incentiveValue ?? 0);
    }
  }, []);

  useEffect(() => {
    if (society) {
      dispatch(getTenderOffers(society._id));
    }
  }, [society]);

  return {
    data: {
      beneficiary,
      society,
      societyValue,
      decimalScale,
      summary,
      incentivesValues,
      vestingValues,
      simulatorSocietyValue,
      valuation,
      hasIncentiveChart,
      isAcceptButtonVisible,
      tenderOffersData,
      hasIncentivesMarketEnabled,
      chartData: convertData(t, chartDataInput),
      chartOptions: chartOptions(t, chartDataInput, { legend: false }),
      isMobile,
    },
    actions: {
      handleSeeOffers,
      handleOpenIncentivesMarket,
      handleChangeSlider,
      handleOpenSellView,
      setPage,
    },
  };
};
